"use client";

import { isWindowDefined } from "@huub-web/utils";
import { useState, useEffect } from "react";

import { useMounted } from "./useMounted";

/**
 * A hook that manages state with localStorage persistence
 * @param key - The localStorage key to use for persistence
 * @param defaultValue - The default value if no stored value exists
 * @param persistedValue - An optional persisted value to use instead of localStorage
 * @returns A tuple containing the current state and a function to update it
 * @todo Make reactive to changes in the localStorage with event listeners
 */
export const useLocalState = <T>(
  key: string,
  defaultValue: T | null = null,
  persistedValue?: T | null,
): [T | null, (value: T | null | ((prev: T | null) => T | null)) => void] => {
  const { isMounted } = useMounted();

  // Always start with the default value during SSR to prevent hydration mismatches
  const [state, setState] = useState<T | null>(
    () => persistedValue ?? defaultValue,
  );

  // Load from localStorage after mount to prevent hydration mismatches
  useEffect(() => {
    if (!isMounted || !isWindowDefined()) {
      return;
    }

    // Only load from localStorage if no persistedValue was provided
    if (persistedValue === undefined) {
      try {
        const stored = localStorage.getItem(key);
        if (stored) {
          const parsedValue = JSON.parse(stored);
          setState(parsedValue);
        }
      } catch (error) {
        console.warn(
          `Failed to load from localStorage for key "${key}":`,
          error,
        );
      }
    }
  }, [isMounted, key, persistedValue]);

  // Save to localStorage when state changes (only after mount)
  useEffect(() => {
    if (!isMounted || !isWindowDefined()) {
      return;
    }

    try {
      if (!localStorage.getItem(key) && state === null) {
        return;
      }
      localStorage.setItem(key, JSON.stringify(state));
    } catch (error) {
      console.warn(`Failed to save to localStorage for key "${key}":`, error);
    }
  }, [isMounted, key, state]);

  return [state, setState];
};
